import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';

import {
  useApolloClient,
  useMutation,
  useSuspenseQuery,
} from '@apollo/client/react';
import { Dialog } from '@mui/material';

import type {
  GetListingEditContextQuery,
  UpdateListingWithPropertyMutation,
  UpdateListingWithPropertyMutationVariables,
} from '@graphql/__generated__/graphql';
import { DialogStickyHeader } from '@modules/_shared/components/DialogStickyHeader';
import { RaForm } from '@modules/_shared/components/form/RaForm';
import {
  type TPricingFormData,
  getListingOverviewEditFormDefinition,
} from '@modules/_shared/components/listing-overview-form/listingOverviewFormDefinition';
import { ListingPriceEditDialog } from '@modules/_shared/components/listing-overview-form/ListingPriceEditDialog';
import { type FormDefinition } from '@modules/_shared/components/listing-overview-form/models';
import type { PropertyFormContext } from '@modules/_shared/components/property-form/forms-definitions/propertyDetailsFormDefinition';
import { PropertyTypeProvider } from '@modules/_shared/components/property-form/propertyTypeProvider';
import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import { stickyDialogSx } from '@modules/_shared/components/stickyDialogSx';
import { useConfirmAgentChange } from '@modules/_shared/hooks/useConfirmAgentChange';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { evictListingWithoutAccess } from '@modules/listings/evictListingWithoutAccess';
import {
  GET_LISTING_EDIT_CONTEXT,
  type GetListingDetailsData,
  UPDATE_LISTING_WITH_PROPERTY,
} from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

interface ListingOverviewEditModalProps {
  listing: GetListingDetailsData;
  opened: boolean;
  editForm: EEditListingForms | null;
  canEdit: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onListedBySaved?: () => Promise<unknown> | void;
  onAppraised?: () => Promise<unknown> | void;
}

export enum EEditListingForms {
  PHOTOS = 'photos',
  MEDIAS = 'medias',
  TITLE_DESCRIPTION = 'title-description',
  ADDRESS = 'address',
  AVAILABILITY = 'availability',
  PRICING = 'pricing',
  PROPERTY = 'property',
  EQUIPMENT = 'equipment',
  RENTED_ROOM = 'rented-room',
  POI = 'poi',
  LISTED_BY = 'listed-by',
  MANDATE = 'mandate',
}

type RaFormWrapperProps<TFormData extends Record<string, any>> = {
  globalDefinition:
    | FormDefinition<TFormData, PropertyFormContext>
    | FormDefinition<TFormData>;
  formContext: PropertyFormContext;
  onCancel: () => void;
  setIsFormDirty: Dispatch<SetStateAction<boolean>>;
  onSuccess: () => void;
  confirmSubmit?: (data: TFormData) => Promise<boolean>;
  gridAlignItems?: 'flex-start' | 'flex-end';
};

const RaFormWrapper = <TFormData extends Record<string, any>>({
  globalDefinition,
  formContext,
  onCancel,
  setIsFormDirty,
  onSuccess,
  confirmSubmit,
  gridAlignItems = 'flex-end',
}: RaFormWrapperProps<TFormData>) => {
  // TODO: Fix the typing error but already spent too much time on it.
  const AnyRaForm = RaForm as React.ComponentType<any>;
  return (
    <AnyRaForm
      formDefinition={globalDefinition.formDefinition}
      onSubmit={async (_: unknown, data: TFormData) => {
        if (confirmSubmit != null && !(await confirmSubmit(data))) {
          return null;
        }

        const result = await globalDefinition.onSubmit(data);
        onSuccess();

        return result;
      }}
      defaultValues={globalDefinition.defaultValues}
      validate={globalDefinition.validate}
      {...(globalDefinition.hasContext ? { context: formContext } : {})}
      onCancel={onCancel}
      onFormDirtyChange={setIsFormDirty}
      actionButtonsSlotProps={{
        container: {
          elevation: 0,
          sx: (theme: { palette: { divider: string } }) => ({
            borderTop: `1px solid ${theme.palette.divider}`,
          }),
        },
      }}
      gridProps={{
        sx: { alignItems: gridAlignItems },
      }}
    >
      <PropertyTypeProvider propertyTypes={formContext.propertyTypes} />
    </AnyRaForm>
  );
};

export const ListingOverviewEditModal: React.FC<
  ListingOverviewEditModalProps
> = ({
  listing,
  opened,
  editForm,
  canEdit,
  onClose,
  onSuccess,
  onListedBySaved,
  onAppraised,
}) => {
  const { t, locale, countryCode } = useLocale();
  const { me } = useAppData();
  const apolloClient = useApolloClient();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { data: context } = useSuspenseQuery<GetListingEditContextQuery>(
    GET_LISTING_EDIT_CONTEXT,
  );
  const [update, { loading }] = useMutation<
    UpdateListingWithPropertyMutation,
    UpdateListingWithPropertyMutationVariables
  >(UPDATE_LISTING_WITH_PROPERTY, {
    update: (cache, { data: mutationResult }) =>
      evictListingWithoutAccess(cache, mutationResult?.update_lots_by_pk),
  });
  const { confirm: confirmAgentChange, dialog: confirmAgentDialog } =
    useConfirmAgentChange();

  const isCHTenant = me?.tenant.country_code === 'CH';

  const listingFormDefinition = useMemo(
    () =>
      getListingOverviewEditFormDefinition(
        t,
        listing,
        update,
        isCHTenant,
        isCHTenant,
        locale,
        countryCode,
        apolloClient,
        me?.id ?? null,
      ),
    [t, listing, update, isCHTenant, locale, countryCode, apolloClient, me?.id],
  );

  const formDefinition =
    editForm == null ? null : listingFormDefinition.get(editForm);

  if (formDefinition == null) {
    return null;
  }

  const formContext: PropertyFormContext = {
    propertyTypes: context.property_types,
    heatingTypes: context.heating_enum,
    heatingDistribution: context.heating_distribution_enum,
    coolingTypes: context.cooling_enum,
    bedTypeOptions: context.bed_type_options,
    windowViewOptions: context.window_view_options,
    tenantGenderOptions: context.tenant_gender_options,
  };

  const handleModalClose = (checkFormDirty = false) => {
    if (loading) {
      // Prevent closing Dialog if saving.
      return;
    }

    if (checkFormDirty && isFormDirty) {
      if (
        window.confirm(
          t('You have unsaved changes. Are you sure you want to close?'),
        ) === false
      ) {
        return;
      }
    }

    onClose();
  };

  const handleClose = () => handleModalClose(true);
  const handleSaved = () => {
    handleModalClose();
    onSuccess();

    if (editForm === EEditListingForms.LISTED_BY) {
      onListedBySaved?.();
    }
  };

  if (editForm === EEditListingForms.PRICING) {
    return (
      <ListingPriceEditDialog
        listing={listing}
        formDefinition={formDefinition as FormDefinition<TPricingFormData>}
        propertyTypes={formContext.propertyTypes}
        opened={opened}
        loading={loading}
        canEdit={canEdit}
        onClose={handleClose}
        onSaved={handleSaved}
        setIsFormDirty={setIsFormDirty}
        onAppraised={onAppraised}
      />
    );
  }

  return (
    <>
      <Dialog
        open={opened}
        fullWidth
        maxWidth={formDefinition.modalMaxWidth ?? 'md'}
        onClose={handleClose}
        sx={stickyDialogSx}
        slots={{
          transition: SlideUpTransition,
        }}
      >
        <DialogStickyHeader
          title={formDefinition.title}
          onClose={handleClose}
        />
        <RaFormWrapper
          // TODO: Fix the typing error but already spent to much time on it.
          globalDefinition={formDefinition as any}
          formContext={formContext}
          onCancel={handleModalClose}
          setIsFormDirty={setIsFormDirty}
          gridAlignItems={
            editForm === EEditListingForms.MEDIAS ? 'flex-start' : 'flex-end'
          }
          onSuccess={handleSaved}
          confirmSubmit={
            editForm === EEditListingForms.LISTED_BY
              ? async data =>
                  listing.broker == null ||
                  data.broker_id === listing.broker.id ||
                  (await confirmAgentChange())
              : undefined
          }
        />
      </Dialog>
      {confirmAgentDialog}
    </>
  );
};
